/**
 * Travel Desk money on the wire (Phase 4, plan §5.8, TD-4.2–4.19): the Zod schemas
 * `travel-settlement.contract.ts` is built from. RN-safe: Zod only.
 *
 * Money is a decimal STRING with its currency beside it (invariant 2) and is never summed across
 * currencies: every total is a list with one row per currency. Dates are `YYYY-MM-DD` calendar
 * days in the traveller's timezone; instants are UTC ISO strings. Every mutable row carries
 * `version` (a stale write is 409 `stale_version`). Every money write also takes an
 * `Idempotency-Key` header (invariant 10): a retry returns the original result.
 *
 * The pure logic these mirror: `travel/money.ts` (netting, money panel, advance limit),
 * `travel/claim-link.ts` (linking, duplicates), `travel/allowance-draft.ts` (per diem, lodging in
 * lieu, mileage drafts), `travel/settlement-rounds.ts` (rounds, closing), `travel/recovery.ts`.
 */

import { z } from 'zod';
import {
  ADVANCE_WRITE_OFF_STATUSES,
  EXPENSE_CATEGORY_KINDS,
  EXPENSE_ADVANCE_RECOVERY_KINDS,
  PAYROLL_RECOVERY_SCHEDULE_KINDS,
  PAYROLL_RECOVERY_SCHEDULE_STATUSES,
  RECOVERY_MANUAL_REASONS,
  TRAVEL_ALLOWANCE_CLAIM_KINDS,
  TRAVEL_DUPLICATE_MATCH_FIELDS,
} from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  expenseAdvanceStatusSchema,
  expenseClaimStatusSchema,
  instantSchema,
  paginationQuerySchema,
  payoutModeSchema,
  uuidSchema,
} from '../schemas/common.js';
import { TRAVEL_CLAIM_LINK_REFUSALS, TRAVEL_MONEY_ERROR_CODES } from './error-codes.js';
import {
  travelAmountSchema,
  travelApprovalContextSchema,
  travelBookingKindSchema,
  travelCityTierSchema,
  travelClockTimeSchema,
  travelDeskBookingStatusSchema,
  travelPerDiemResultSchema,
  travelPersonRefSchema,
  travelPolicySnapshotSchema,
  travelPositiveAmountSchema,
  travelSettlementKindSchema,
  travelSettlementLineDecisionSchema,
  travelSettlementStatusSchema,
  travelTripStatusSchema,
  travelVersionSchema,
} from './schemas.js';

// ---------------------------------------------------------------------------
// Enum schemas
// ---------------------------------------------------------------------------

export const travelAllowanceClaimKindSchema = z.enum(TRAVEL_ALLOWANCE_CLAIM_KINDS);
export const travelDuplicateMatchFieldSchema = z.enum(TRAVEL_DUPLICATE_MATCH_FIELDS);
export const expenseAdvanceRecoveryKindSchema = z.enum(EXPENSE_ADVANCE_RECOVERY_KINDS);
export const recoveryManualReasonSchema = z.enum(RECOVERY_MANUAL_REASONS);
export const payrollRecoveryScheduleStatusSchema = z.enum(PAYROLL_RECOVERY_SCHEDULE_STATUSES);
export const payrollRecoveryScheduleKindSchema = z.enum(PAYROLL_RECOVERY_SCHEDULE_KINDS);
export const expenseCategoryKindSchema = z.enum(EXPENSE_CATEGORY_KINDS);
export const advanceWriteOffStatusSchema = z.enum(ADVANCE_WRITE_OFF_STATUSES);
export const travelClaimLinkRefusalSchema = z.enum(TRAVEL_CLAIM_LINK_REFUSALS);
export const travelMoneyErrorCodeSchema = z.enum(TRAVEL_MONEY_ERROR_CODES);

/** `?flag=true` / `?flag=false` (a plain `z.coerce.boolean()` reads "false" as true). */
const queryBoolean = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();

const uniqueIds = <T extends z.ZodTypeAny>(schema: z.ZodArray<T>, message: string) =>
  schema.refine((ids) => new Set(ids as unknown[]).size === ids.length, { message });

// ---------------------------------------------------------------------------
// Advances on the trip (TD-4.2, 4.14, 4.15)
// ---------------------------------------------------------------------------

/** One advance as the trip's money panel shows it. */
export const tripMoneyAdvanceSchema = z.object({
  id: uuidSchema,
  /** Null on a draft. */
  reference: z.string().nullable(),
  status: expenseAdvanceStatusSchema,
  currency: currencySchema,
  requestedAmount: decimalStringSchema,
  /** Null until paid out. */
  outstandingAmount: decimalStringSchema.nullable(),
  disbursedAt: instantSchema.nullable(),
  /** The settle-by day (trip end + `settleWithinDays`). */
  expectedSettlementDate: dateOnlySchema.nullable(),
  /** Past its settle-by day with money still outstanding. */
  overdue: z.boolean(),
  /** When the traveller accepted the recovery consent (D22). */
  recoveryConsentAt: instantSchema.nullable(),
});
export type TripMoneyAdvance = z.infer<typeof tripMoneyAdvanceSchema>;

/** Why a new advance cannot be asked for right now (the form shows it instead of the button). */
export const tripAdvanceBlockSchema = z.object({
  code: z.enum(['trip_not_approved', 'trip_advance_exists', 'advance_blocked_overdue']),
  /** For `advance_blocked_overdue`: the overdue advances' references. */
  advanceReferences: z.array(z.string()),
  message: z.string(),
});

/** What the "Ask for an advance" form opens with. */
export const tripAdvanceDraftSchema = z.object({
  travelRequestId: uuidSchema,
  /** The trip's currency; the advance is in it. */
  currency: currencySchema,
  estimatedAmount: decimalStringSchema.nullable(),
  /** `tripAdvanceLimit(...)`: null is uncapped by the travel policy. */
  limit: decimalStringSchema.nullable(),
  /** The amount the form pre-fills: the limit, else the estimate, else null. */
  suggestedAmount: decimalStringSchema.nullable(),
  /** True when an amount within `limit` is approved at once, without a manager step (TD-4.14). */
  autoApproveWithinLimit: z.boolean(),
  /** The text the traveller accepts (the policy's `recoveryConsentText`, else the product default). */
  consentText: z.string(),
  existingAdvance: tripMoneyAdvanceSchema.nullable(),
  /** Null when an advance may be asked for. */
  blocked: tripAdvanceBlockSchema.nullable(),
});
export type TripAdvanceDraft = z.infer<typeof tripAdvanceDraftSchema>;

/**
 * Ask for an advance against an approved trip. `recoveryConsentAccepted` must be true (422
 * `recovery_consent_required` otherwise): the server records the instant and the text shown.
 */
export const requestTripAdvanceBodySchema = z.object({
  amount: travelPositiveAmountSchema,
  /** Must be the trip's currency (422 `advance_currency_mismatch`). */
  currency: currencySchema,
  /** Defaults to "Advance for trip <reference>". */
  purpose: z.string().trim().min(1).max(500).optional(),
  recoveryConsentAccepted: z.boolean(),
});
export type RequestTripAdvanceBody = z.infer<typeof requestTripAdvanceBodySchema>;

export const tripAdvanceResultSchema = z.object({
  advance: tripMoneyAdvanceSchema,
  /** True: within the policy limit, approved without a manager step (TD-4.14). */
  autoApproved: z.boolean(),
});
export type TripAdvanceResult = z.infer<typeof tripAdvanceResultSchema>;

// ---------------------------------------------------------------------------
// Claims on the trip (TD-4.3, 4.12, 4.17)
// ---------------------------------------------------------------------------

export const bookingDuplicateMatchSchema = z.object({
  bookingId: uuidSchema,
  matchedOn: z.array(travelDuplicateMatchFieldSchema),
  /** "Flight DEL–BOM, 12 Oct, PNR AB12CD": a label the route builds. */
  bookingSummary: z.string(),
});
export type BookingDuplicateMatchDto = z.infer<typeof bookingDuplicateMatchSchema>;

/** A trip-linked claim as the trip, the settlement and the money panel show it. */
export const tripLinkedClaimSchema = z.object({
  id: uuidSchema,
  reference: z.string().nullable(),
  categoryId: uuidSchema,
  categoryName: z.string(),
  /** Set when the claim was drafted from the policy (per diem, lodging in lieu, mileage). */
  allowanceKind: travelAllowanceClaimKindSchema.nullable(),
  expenseDate: dateOnlySchema,
  merchantName: z.string(),
  businessPurpose: z.string(),
  billNumber: z.string().nullable(),
  amount: decimalStringSchema,
  /** What the approver authorised, when lower than claimed. */
  approvedAmount: decimalStringSchema.nullable(),
  currency: currencySchema,
  status: expenseClaimStatusSchema,
  /** The round it went in; null while in none. */
  settlementId: uuidSchema.nullable(),
  settlementRound: z.number().int().positive().nullable(),
  settlementKind: travelSettlementKindSchema.nullable(),
  decision: travelSettlementLineDecisionSchema.nullable(),
  advanceAppliedAmount: decimalStringSchema.nullable(),
  /** Company-paid bookings this looks like (a flag for the approver, never a block). */
  duplicateOfBookings: z.array(bookingDuplicateMatchSchema),
  /** The claim no longer fits the trip (dates moved after it was filed): null when it does. */
  linkProblem: travelClaimLinkRefusalSchema.nullable(),
  /** The self-booked booking this claim pays for (`ExpenseClaim.travelBookingId`), if any. */
  travelBookingId: uuidSchema.nullable(),
  /** A self-booked fare paid before the trip (D29): exempt from the date window. */
  isPreTripFare: z.boolean(),
  hasReceipt: z.boolean(),
});
export type TripLinkedClaim = z.infer<typeof tripLinkedClaimSchema>;

/** A booking the company paid through the desk, shown on the claim form so it is not claimed again. */
export const tripCompanyPaidBookingSchema = z.object({
  bookingId: uuidSchema,
  kind: travelBookingKindSchema,
  summary: z.string(),
  bookingReference: z.string().nullable(),
  status: travelDeskBookingStatusSchema,
  amount: decimalStringSchema.nullable(),
  taxAmount: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  travelDate: dateOnlySchema.nullable(),
});
export type TripCompanyPaidBooking = z.infer<typeof tripCompanyPaidBookingSchema>;

export const tripClaimsSchema = z.object({
  travelRequestId: uuidSchema,
  /** D32: claims dated in [from, to] link (pre-trip fares excepted). */
  claimWindow: z.object({ from: dateOnlySchema, to: dateOnlySchema }),
  claims: z.array(tripLinkedClaimSchema),
  companyPaidBookings: z.array(tripCompanyPaidBookingSchema),
});
export type TripClaims = z.infer<typeof tripClaimsSchema>;

/** The claim form's live check, before saving: does this look like a booking the company paid? */
export const checkClaimDuplicateQuerySchema = z.object({
  amount: travelAmountSchema,
  currency: currencySchema,
  expenseDate: dateOnlySchema,
  billNumber: z.string().trim().max(100).optional(),
});

export const claimDuplicateCheckSchema = z.object({
  matches: z.array(bookingDuplicateMatchSchema),
});

/** The trips a claim may be linked to (the claim form's trip picker). */
export const listLinkableTripsQuerySchema = z.object({
  /** Narrows to trips whose claim window holds this day. */
  expenseDate: dateOnlySchema.optional(),
  /** A self-booked fare paid before the trip: the date window does not apply (D29). */
  preTripFare: queryBoolean,
  /** Filing for someone else (`expense.claim:file_for` over them, 403 otherwise). Defaults to the caller. */
  personId: uuidSchema.optional(),
});

export const linkableTripSchema = z.object({
  id: uuidSchema,
  reference: z.string().nullable(),
  destination: z.string(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  status: travelTripStatusSchema,
  currency: currencySchema,
});
export type LinkableTrip = z.infer<typeof linkableTripSchema>;

// ---------------------------------------------------------------------------
// Allowance drafts (TD-4.4, 4.16)
// ---------------------------------------------------------------------------

export const allowanceDraftStaySchema = z.object({
  city: z.string(),
  checkIn: dateOnlySchema,
  checkOut: dateOnlySchema,
  cityTier: travelCityTierSchema.nullable().optional(),
  lodgingInLieu: z.boolean().optional(),
});

/** `AllowanceDraftTrip` (`travel/allowance-draft.ts`) on the wire: the trip as travelled. */
export const allowanceDraftTripSchema = z.object({
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  departureTime: travelClockTimeSchema.nullable().optional(),
  returnTime: travelClockTimeSchema.nullable().optional(),
  gradeRank: z.number().int().nullable(),
  stays: z.array(allowanceDraftStaySchema),
  destinationCity: z.string().nullable().optional(),
  personalDays: z.array(dateOnlySchema),
});

export const mealsProvidedSchema = z.object({
  date: dateOnlySchema,
  breakfast: z.boolean().optional(),
  lunch: z.boolean().optional(),
  dinner: z.boolean().optional(),
});

export const mileageEntrySchema = z.object({
  date: dateOnlySchema,
  fromCity: z.string().trim().min(1).max(120),
  toCity: z.string().trim().min(1).max(120),
  distanceKm: travelPositiveAmountSchema,
});
export type MileageEntryDto = z.infer<typeof mileageEntrySchema>;

export const allowanceClaimDraftSchema = z.object({
  kind: travelAllowanceClaimKindSchema,
  amount: decimalStringSchema,
  currency: currencySchema,
  expenseDate: dateOnlySchema,
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  distanceKm: decimalStringSchema.nullable(),
  ratePerKm: decimalStringSchema.nullable(),
  description: z.string(),
});

export const allowanceDraftWarningSchema = z.object({
  code: z.enum([
    'grade_missing',
    'per_diem_not_configured',
    'rate_missing',
    'mileage_rate_missing',
    'mileage_outside_trip',
    'mileage_on_personal_day',
    'already_claimed',
  ]),
  kind: travelAllowanceClaimKindSchema.nullable(),
  dates: z.array(dateOnlySchema),
  message: z.string(),
});

/**
 * The drafts for a finished trip, before meals and mileage are entered. `inputs` is exactly what
 * the server priced it with, so the form re-runs `buildAllowanceDraft` live as the traveller ticks
 * meals or adds mileage (the same function, as the policy check does); the server recomputes on
 * `createAllowanceClaims` and never takes an amount from the client. `inputs` is null when no
 * policy applies.
 */
export const tripAllowanceDraftSchema = z.object({
  travelRequestId: uuidSchema,
  currency: currencySchema,
  gradeMissing: z.boolean(),
  /** Exactly `computePerDiem`'s output. */
  perDiem: travelPerDiemResultSchema.nullable(),
  claims: z.array(allowanceClaimDraftSchema),
  warnings: z.array(allowanceDraftWarningSchema),
  alreadyClaimed: z.object({
    perDiem: z.boolean(),
    lodgingInLieu: z.boolean(),
    mileageDates: z.array(dateOnlySchema),
  }),
  inputs: z
    .object({
      trip: allowanceDraftTripSchema,
      policy: travelPolicySnapshotSchema,
    })
    .nullable(),
});
export type TripAllowanceDraft = z.infer<typeof tripAllowanceDraftSchema>;

/**
 * File the drafts as DRAFT claims on the trip (the traveller then puts them in a round). Only the
 * kinds listed are filed; meals provided and mileage entries change the amounts, which the server
 * computes.
 */
export const createAllowanceClaimsBodySchema = z.object({
  kinds: uniqueIds(z.array(travelAllowanceClaimKindSchema).min(1).max(3), 'A kind can be listed once'),
  mealsProvided: z.array(mealsProvidedSchema).max(400).optional(),
  mileage: z.array(mileageEntrySchema).max(100).optional(),
});
export type CreateAllowanceClaimsBody = z.infer<typeof createAllowanceClaimsBodySchema>;

export const createdAllowanceClaimsSchema = z.object({
  claims: z.array(tripLinkedClaimSchema),
  draft: tripAllowanceDraftSchema,
});

// ---------------------------------------------------------------------------
// Settlement rounds (TD-4.5, 4.13, 4.15, 4.18)
// ---------------------------------------------------------------------------

/** One expense in a round, with the approver's decision and (once settled) the net-off. */
export const travelSettlementLineSchema = z.object({
  expenseClaimId: uuidSchema,
  claimReference: z.string().nullable(),
  categoryName: z.string(),
  allowanceKind: travelAllowanceClaimKindSchema.nullable(),
  businessPurpose: z.string(),
  merchantName: z.string(),
  expenseDate: dateOnlySchema,
  amount: decimalStringSchema,
  currency: currencySchema,
  decision: travelSettlementLineDecisionSchema,
  /** Null: approved as claimed (or not decided). */
  approvedAmount: decimalStringSchema.nullable(),
  /** Required on a rejection. */
  reason: z.string().nullable(),
  decidedById: uuidSchema.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  /** After net-off: the advance it drew on, what that covered, and what is paid to the traveller. */
  advanceId: uuidSchema.nullable(),
  advanceAppliedAmount: decimalStringSchema.nullable(),
  payableAmount: decimalStringSchema.nullable(),
  duplicateOfBookings: z.array(bookingDuplicateMatchSchema),
  linkProblem: travelClaimLinkRefusalSchema.nullable(),
  hasReceipt: z.boolean(),
});
export type TravelSettlementLine = z.infer<typeof travelSettlementLineSchema>;

/** A round's money in one currency. Decided figures are null until decided / settled. */
export const travelSettlementTotalSchema = z.object({
  currency: currencySchema,
  claimed: decimalStringSchema,
  approved: decimalStringSchema.nullable(),
  rejected: decimalStringSchema.nullable(),
  /** Covered by advances at net-off. */
  netted: decimalStringSchema.nullable(),
  /** Paid to the traveller after net-off (through the normal claim payout). */
  payable: decimalStringSchema.nullable(),
  /** Still outstanding on the trip's own advances after net-off. */
  tripAdvanceOutstanding: decimalStringSchema.nullable(),
});
export type TravelSettlementTotal = z.infer<typeof travelSettlementTotalSchema>;

/** What net-off did to one advance. */
export const travelSettlementNettedAdvanceSchema = z.object({
  advanceId: uuidSchema,
  advanceReference: z.string().nullable(),
  currency: currencySchema,
  /** The trip's own advance; false for another overdue advance (EC-37). */
  ownTrip: z.boolean(),
  outstandingBefore: decimalStringSchema,
  applied: decimalStringSchema,
  outstandingAfter: decimalStringSchema,
});

/** The round, without its lines: list rows. */
export const travelSettlementSummarySchema = z.object({
  id: uuidSchema,
  travelRequestId: uuidSchema,
  tripReference: z.string().nullable(),
  travellerPersonId: uuidSchema,
  travellerName: z.string(),
  /** 1-based, per trip. */
  round: z.number().int().positive(),
  kind: travelSettlementKindSchema,
  status: travelSettlementStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  totals: z.array(travelSettlementTotalSchema),
  lineCount: z.number().int().nonnegative(),
  rejectedLineCount: z.number().int().nonnegative(),
  /** The trip's approved estimate, in the trip's currency. */
  estimatedAmount: decimalStringSchema.nullable(),
  tripCurrency: currencySchema,
  /** Percent over the estimate (trip currency only), one decimal place; null when not over. */
  overEstimatePct: z.string().nullable(),
  submittedAt: instantSchema.nullable(),
  decidedAt: instantSchema.nullable(),
  settledAt: instantSchema.nullable(),
  version: travelVersionSchema,
  createdAt: instantSchema,
});
export type TravelSettlementSummary = z.infer<typeof travelSettlementSummarySchema>;

/**
 * One round in full. Both the manager when it was submitted and the current one are shown when
 * they differ (EC-25). `approval` is the same approver view a trip carries (current step, the
 * question thread); its `violations` are empty for a round.
 */
export const travelSettlementSchema = travelSettlementSummarySchema.extend({
  /** The traveller's note on submit. */
  comment: z.string().nullable(),
  /** The approver's final comment. */
  decisionComment: z.string().nullable(),
  submittedToManagers: z.array(travelPersonRefSchema),
  currentManagers: z.array(travelPersonRefSchema),
  approval: travelApprovalContextSchema.optional(),
  lines: z.array(travelSettlementLineSchema),
  /** After net-off; empty before. */
  nettedAdvances: z.array(travelSettlementNettedAdvanceSchema),
  /** Approved-but-unpaid trip advances cancelled at net-off (EC-36). */
  cancelledAdvanceIds: z.array(uuidSchema),
});
export type TravelSettlement = z.infer<typeof travelSettlementSchema>;

export const travelSettlementDraftQuerySchema = z.object({
  kind: travelSettlementKindSchema.default('main'),
});

/** The "Submit trip expenses" screen: what may go in the round and what net-off would do. */
export const travelSettlementDraftSchema = z.object({
  travelRequestId: uuidSchema,
  kind: travelSettlementKindSchema,
  /** Null when the round may be submitted; else the code submit would refuse with. */
  refusal: travelMoneyErrorCodeSchema.nullable(),
  /** Trip-linked claims in no round yet (drafts and loose submitted ones). */
  eligibleClaims: z.array(tripLinkedClaimSchema),
  /** If every eligible claim were approved as claimed: one row per currency. */
  projection: z.array(
    z.object({
      currency: currencySchema,
      claimed: decimalStringSchema,
      netted: decimalStringSchema,
      payable: decimalStringSchema,
      tripAdvanceOutstanding: decimalStringSchema,
    }),
  ),
  /** The last day the trip takes expenses. */
  expenseWindowEnd: dateOnlySchema.nullable(),
  /** Send it back as `tripVersion`. */
  tripVersion: travelVersionSchema,
});
export type TravelSettlementDraft = z.infer<typeof travelSettlementDraftSchema>;

/**
 * Submit a round. Draft claims in it are submitted with it (they never go through the single-claim
 * submit, D9). `noMoreExpenses` also tells the trip nothing more is coming (D13), so it closes
 * once this round is settled and nothing is outstanding.
 */
export const submitTravelSettlementBodySchema = z.object({
  kind: travelSettlementKindSchema,
  expenseClaimIds: uniqueIds(z.array(uuidSchema).min(1).max(200), 'A claim can be listed once'),
  comment: z.string().trim().max(1000).optional(),
  noMoreExpenses: z.boolean().default(false),
  /** The trip's token (409 `stale_version`). */
  tripVersion: travelVersionSchema,
});
export type SubmitTravelSettlementBody = z.infer<typeof submitTravelSettlementBodySchema>;

export const withdrawTravelSettlementBodySchema = z.object({
  version: travelVersionSchema,
  reason: z.string().trim().max(500).optional(),
});

/** One per-expense decision (D8). A line not listed is approved as claimed. */
export const travelSettlementLineDecisionInputSchema = z
  .object({
    expenseClaimId: uuidSchema,
    decision: z.enum(['approved', 'rejected']),
    /** Approve less than claimed (422 `approved_amount_exceeds_claim` when more). */
    approvedAmount: travelAmountSchema.optional(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .refine((line) => line.decision === 'approved' || line.reason !== undefined, {
    message: 'A rejected expense needs a reason',
    path: ['reason'],
  })
  .refine((line) => line.decision === 'approved' || line.approvedAmount === undefined, {
    message: 'Only an approved expense carries an approved amount',
    path: ['approvedAmount'],
  });

/**
 * Approve the step awaiting the caller. An empty `lines` approves every expense as claimed (the
 * inbox one-click). The per-expense decisions are written with the step decision in one
 * transaction; on the last step the round becomes `approved` and finance nets it off.
 */
export const approveTravelSettlementBodySchema = z
  .object({
    version: travelVersionSchema,
    comment: z.string().trim().max(1000).optional(),
    lines: z.array(travelSettlementLineDecisionInputSchema).max(200).default([]),
  })
  .refine((body) => new Set(body.lines.map((l) => l.expenseClaimId)).size === body.lines.length, {
    message: 'An expense can be decided once',
    path: ['lines'],
  });
export type ApproveTravelSettlementBody = z.infer<typeof approveTravelSettlementBodySchema>;

/** Reject the whole round: every expense goes back to the traveller as a draft. */
export const rejectTravelSettlementBodySchema = z.object({
  version: travelVersionSchema,
  comment: z.string().trim().min(1).max(1000),
});

export const travelSettlementQuestionBodySchema = z.object({
  question: z.string().trim().min(1).max(1000),
});

export const travelSettlementAnswerBodySchema = z.object({
  answer: z.string().trim().min(1).max(2000),
});

/**
 * Finance's batch net-off of an approved round (one transaction, advisory locks on each advance
 * and claim). `includeOtherOverdueAdvances` also nets the traveller's other overdue advances in the
 * same currency after the trip's own (EC-37).
 */
export const netOffTravelSettlementBodySchema = z.object({
  version: travelVersionSchema,
  includeOtherOverdueAdvances: z.boolean().default(false),
});

/** Finance's review list. */
export const listTravelSettlementsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to `approved` (waiting for net-off). */
  status: travelSettlementStatusSchema.optional(),
  kind: travelSettlementKindSchema.optional(),
  currency: currencySchema.optional(),
  personId: uuidSchema.optional(),
  travelRequestId: uuidSchema.optional(),
  /** Free text over the trip reference, destination and the traveller's name. */
  q: z.string().trim().min(1).max(120).optional(),
});

// ---------------------------------------------------------------------------
// The trip's money panel (TD-4.10)
// ---------------------------------------------------------------------------

/** `TripMoneyTotals` (`travel/money.ts`) on the wire: one currency. */
export const tripMoneyTotalsSchema = z.object({
  currency: currencySchema,
  advanceRequested: decimalStringSchema,
  advancePaid: decimalStringSchema,
  advanceOutstanding: decimalStringSchema,
  claimDraft: decimalStringSchema,
  claimPending: decimalStringSchema,
  claimApproved: decimalStringSchema,
  claimSettled: decimalStringSchema,
  claimRejected: decimalStringSchema,
  nettedFromAdvance: decimalStringSchema,
  companyPaid: decimalStringSchema,
  /** Signed: positive is owed to the traveller, negative is owed back by them. */
  netPosition: decimalStringSchema,
  netPayable: decimalStringSchema,
  netRecoverable: decimalStringSchema,
});
export type TripMoneyTotalsDto = z.infer<typeof tripMoneyTotalsSchema>;

export const tripMoneySummarySchema = z.object({
  travelRequestId: uuidSchema,
  tripReference: z.string().nullable(),
  tripStatus: travelTripStatusSchema,
  /** The trip's currency: listed first in `totals`. */
  currency: currencySchema,
  /** One row per currency; never summed across currencies. */
  totals: z.array(tripMoneyTotalsSchema),
  advances: z.array(tripMoneyAdvanceSchema),
  settlements: z.array(travelSettlementSummarySchema),
  /** The rounds the traveller may submit now (`settlementRoundRefusal` is null for them). */
  submittableRounds: z.array(travelSettlementKindSchema),
  canRequestAdvance: z.boolean(),
  /** The last day the trip takes expenses; null before the trip ends. */
  expenseWindowEnd: dateOnlySchema.nullable(),
  noMoreExpensesAt: instantSchema.nullable(),
  /** The trip's token, for `noMoreExpenses` and `submitSettlement`. */
  tripVersion: travelVersionSchema,
});
export type TripMoneySummary = z.infer<typeof tripMoneySummarySchema>;

// ---------------------------------------------------------------------------
// Recovery (TD-4.6 read side, 4.19) and write-off (TD-4.8)
// ---------------------------------------------------------------------------

export const advanceRecoveryRowSchema = z.object({
  id: uuidSchema,
  kind: expenseAdvanceRecoveryKindSchema,
  /** Always positive; a `reversal` gives this back. */
  amount: decimalStringSchema,
  currency: currencySchema,
  recordedAt: instantSchema,
  recordedById: uuidSchema.nullable(),
  recordedByName: z.string().nullable(),
  note: z.string().nullable(),
  /** `claim_net_off`: the claim absorbed. */
  expenseClaimId: uuidSchema.nullable(),
  /** `payroll_deduction` / `reversal`: the run and its period. */
  payrollRunId: uuidSchema.nullable(),
  periodLabel: z.string().nullable(),
  /** `reversal`: the row it undoes. */
  reversesRecoveryId: uuidSchema.nullable(),
  /** Set on a row that was reversed. */
  reversedByRecoveryId: uuidSchema.nullable(),
});
export type AdvanceRecoveryRow = z.infer<typeof advanceRecoveryRowSchema>;

/**
 * One row of the advance's payroll schedule (D22): a deduction (or an arrears refund of a wrong
 * one, EC-58) on a run. `recovered` rows are the history; `scheduled` is on its way; `removed` was
 * taken off before finalize; `manual` is finance's to recover by hand (`manualReason`).
 */
export const scheduledPayrollRecoverySchema = z.object({
  id: uuidSchema,
  kind: payrollRecoveryScheduleKindSchema,
  status: payrollRecoveryScheduleStatusSchema,
  /** 1 for the first run, +1 for each carry-forward. */
  sequence: z.number().int().positive(),
  payrollVariableInputId: uuidSchema.nullable(),
  payrollRunId: uuidSchema.nullable(),
  /** "November 2026"; null while no run is chosen (a `manual` row). */
  periodLabel: z.string().nullable(),
  amount: decimalStringSchema,
  currency: currencySchema,
  manualReason: recoveryManualReasonSchema.nullable(),
  scheduledAt: instantSchema,
  scheduledById: uuidSchema.nullable(),
  recoveredAt: instantSchema.nullable(),
});
export type ScheduledPayrollRecovery = z.infer<typeof scheduledPayrollRecoverySchema>;

export const advanceWriteOffSchema = z.object({
  id: uuidSchema,
  advanceId: uuidSchema,
  advanceReference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  travelRequestId: uuidSchema.nullable(),
  currency: currencySchema,
  amount: decimalStringSchema,
  reason: z.string(),
  status: advanceWriteOffStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  proposedById: uuidSchema,
  proposedByName: z.string().nullable(),
  proposedAt: instantSchema,
  decidedById: uuidSchema.nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  decisionComment: z.string().nullable(),
  version: travelVersionSchema,
});
export type AdvanceWriteOff = z.infer<typeof advanceWriteOffSchema>;

/** What payroll recovery would do next (`planPayrollRecovery`), and the instalments the cap implies. */
export const advanceRecoveryPlanSchema = z.object({
  kind: z.enum(['deduct', 'manual', 'nothing']),
  amount: decimalStringSchema.nullable(),
  carryForward: decimalStringSchema.nullable(),
  cap: decimalStringSchema.nullable(),
  manualReason: recoveryManualReasonSchema.nullable(),
  /** `[cap, cap, …, rest]` at today's gross. */
  projectedInstalments: z.array(decimalStringSchema),
  projectedRemainder: decimalStringSchema,
});

/**
 * One advance's recovery: what was paid out, every ledger row, what is scheduled on payroll, and
 * the plan. Salary is NOT in it: only the cap derived from it, and only finance sees the plan
 * (the traveller's view leaves `plan` null).
 */
export const advanceRecoveryViewSchema = z.object({
  advanceId: uuidSchema,
  advanceReference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  travelRequestId: uuidSchema.nullable(),
  tripReference: z.string().nullable(),
  status: expenseAdvanceStatusSchema,
  currency: currencySchema,
  disbursedAmount: decimalStringSchema.nullable(),
  outstandingAmount: decimalStringSchema,
  expectedSettlementDate: dateOnlySchema.nullable(),
  overdue: z.boolean(),
  daysOverdue: z.number().int().nonnegative().nullable(),
  recoveryConsentAt: instantSchema.nullable(),
  ledger: z.array(advanceRecoveryRowSchema),
  scheduled: z.array(scheduledPayrollRecoverySchema),
  plan: advanceRecoveryPlanSchema.nullable(),
  pendingWriteOff: advanceWriteOffSchema.nullable(),
});
export type AdvanceRecoveryView = z.infer<typeof advanceRecoveryViewSchema>;

/** Finance's outstanding-advances register (the recovery queue). */
export const listOutstandingAdvancesQuerySchema = paginationQuerySchema.extend({
  overdueOnly: queryBoolean,
  /** Only advances linked to a trip. */
  travelOnly: queryBoolean,
  currency: currencySchema.optional(),
  personId: uuidSchema.optional(),
  q: z.string().trim().min(1).max(120).optional(),
});

export const outstandingAdvanceRowSchema = z.object({
  advanceId: uuidSchema,
  advanceReference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  travelRequestId: uuidSchema.nullable(),
  tripReference: z.string().nullable(),
  currency: currencySchema,
  outstandingAmount: decimalStringSchema,
  expectedSettlementDate: dateOnlySchema.nullable(),
  daysOverdue: z.number().int().nonnegative().nullable(),
  /** On unfinalized payroll inputs. */
  scheduledAmount: decimalStringSchema,
  pendingWriteOff: z.boolean(),
  /** Set when payroll cannot recover it automatically. */
  manualOnlyReason: recoveryManualReasonSchema.nullable(),
});
export type OutstandingAdvanceRow = z.infer<typeof outstandingAdvanceRowSchema>;

/**
 * Recover through payroll (D22): adds a `one_time_deduction` input, labelled with the advance's
 * reference, to the next unlocked run of the traveller's current legal entity, capped per run.
 * No body fields: the amount is the plan's, never the caller's.
 */
export const recoverAdvanceViaPayrollBodySchema = z.object({});

/** Money the traveller handed back (cash, bank transfer): a `repayment` ledger row. */
export const recordAdvanceRepaymentBodySchema = z.object({
  amount: travelPositiveAmountSchema,
  /** The day it was received. */
  receivedOn: dateOnlySchema,
  mode: payoutModeSchema,
  reference: z.string().trim().max(120).optional(),
});

/**
 * Undo a recovery row (EC-58). A finalized `payroll_deduction` is refunded through an `arrears`
 * input on the next run and a `reversal` row; a `repayment` or `claim_net_off` gets a `reversal` row.
 */
export const reverseAdvanceRecoveryBodySchema = z.object({
  reason: z.string().trim().min(5).max(1000),
});

/** Finance proposes; the org owner decides through the workflow (TD-4.8). */
export const proposeAdvanceWriteOffBodySchema = z.object({
  /** Defaults to everything outstanding (422 `amount_exceeds_outstanding` when more). */
  amount: travelPositiveAmountSchema.optional(),
  reason: z.string().trim().min(10).max(1000),
});

export const decideAdvanceWriteOffBodySchema = z
  .object({
    version: travelVersionSchema,
    decision: z.enum(['approve', 'reject']),
    comment: z.string().trim().max(1000).optional(),
  })
  .refine((body) => body.decision === 'approve' || (body.comment ?? '').length > 0, {
    message: 'A rejection needs a reason',
    path: ['comment'],
  });

export const withdrawAdvanceWriteOffBodySchema = z.object({
  version: travelVersionSchema,
});

export const listAdvanceWriteOffsQuerySchema = paginationQuerySchema.extend({
  status: advanceWriteOffStatusSchema.optional(),
  /** Only those with a workflow step waiting on the caller. */
  awaitingMyDecision: queryBoolean,
});
